import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CampaignsPanel from "@/components/admin/campaigns/CampaignsPanel";
import SendQueueCampaignsPanel from "@/components/admin/sendQueueCampaigns/SendQueueCampaignsPanel";
import TabButton from "@/components/usersOrgs/TabButton";

/**
 * GFG Admin's Campaign Manager — not built yet. Mirrors the reference demo's admin tab bar,
 * which splits this surface into "Send Queue" (scheduled/in-flight sends) and "Campaigns"
 * (the campaign list) as two separate tabs. Client users never see this — they get the real
 * Campaigns page at the same route (see App.tsx).
 */
type CampaignManagerTab = "queue" | "campaigns";

const TABS: { key: CampaignManagerTab; label: string }[] = [
  { key: "queue", label: "Send Queue" },
  { key: "campaigns", label: "Campaigns" },
];

/**
 * Router state is untyped on the wire, so validate the shape before trusting it and resolve the
 * name against TABS — an unknown tab name is dropped rather than left to blank the panel.
 */
function readTab(state: unknown): CampaignManagerTab | null {
  if (typeof state !== "object" || state === null) return null;
  const { tab } = state as Record<string, unknown>;
  return TABS.find((t) => t.key === tab)?.key ?? null;
}

export default function CampaignManager() {
  const [tab, setTab] = useState<CampaignManagerTab>("queue");
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Deep links from the Send Queue land here: the Campaign cell and the row menu's "View
   * campaign" both `navigate("/campaigns", { state })`, which is the route this component
   * already renders — so nothing moves unless this component reads the message itself.
   *
   * Keyed on `location`, whose identity is a new one per history entry: navigating twice to
   * the SAME tab is two navigations and therefore two runs, where keying on the tab name
   * alone would make the second visit a no-op. Manual tab clicks push no history entry, so
   * they are left alone.
   *
   * The message is then CONSUMED — replaced with an empty state on the current entry. Router
   * state is a one-shot event, not a value: left in place it would replay on every remount of
   * this component and on every Back into this entry, yanking the tab away from wherever the
   * user had since put it. Clearing it re-runs this effect once with nothing to read, which
   * stops there. A plain visit to /campaigns carries no state and skips all of it.
   */
  useEffect(() => {
    const requested = readTab(location.state);
    if (!requested) return;
    setTab(requested);
    // Same URL, minus the message — `search`/`hash` are carried over so consuming the message
    // can never be mistaken for a navigation.
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [location, navigate]);

  return (
    <div>
      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 24,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {TABS.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </div>
      {tab === "queue" ? <SendQueueCampaignsPanel /> : <CampaignsPanel />}
    </div>
  );
}
